/* Geracao das placas em PDF (mesmo layout do painel do dono), para uso no Painel Master. */
import { jsPDF } from 'jspdf';

const hexToRgb = (hex) => {
  const h = (hex || '#000080').replace('#', '');
  return [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)];
};
const luminance = (rgb) => (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;

const loadFonts = async (pdf) => {
  const toBase64 = (buf) => {
    const bytes = new Uint8Array(buf);
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin);
  };
  try {
    const [regRes, boldRes] = await Promise.all([fetch('/fonts/roboto-regular.ttf'), fetch('/fonts/roboto-bold.ttf')]);
    if (!regRes.ok || !boldRes.ok) throw new Error('Font fetch failed');
    const [regBuf, boldBuf] = await Promise.all([regRes.arrayBuffer(), boldRes.arrayBuffer()]);
    pdf.addFileToVFS('Roboto-Regular.ttf', toBase64(regBuf)); pdf.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    pdf.addFileToVFS('Roboto-Bold.ttf', toBase64(boldBuf)); pdf.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
    return 'Roboto';
  } catch { return 'helvetica'; }
};

const getQRCodeImage = async (url, resolution = 1200) => {
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute'; tempDiv.style.left = '-9999px';
  document.body.appendChild(tempDiv);
  const { createRoot } = await import('react-dom/client');
  const { QRCodeSVG } = await import('qrcode.react');
  const { createElement } = await import('react');
  const root = createRoot(tempDiv);
  root.render(createElement(QRCodeSVG, {
    value: url, size: resolution, level: 'H', includeMargin: true, fgColor: '#000000', bgColor: '#FFFFFF'
  }));
  await new Promise(r => setTimeout(r, 400));
  const svg = tempDiv.querySelector('svg');
  if (!svg) { root.unmount(); document.body.removeChild(tempDiv); return null; }
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const svgData = new XMLSerializer().serializeToString(svg);
  const img = new Image();
  await new Promise((resolve, reject) => {
    img.onload = resolve; img.onerror = reject;
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  });
  canvas.width = resolution; canvas.height = resolution;
  ctx.fillStyle = 'white'; ctx.fillRect(0, 0, resolution, resolution);
  ctx.drawImage(img, 0, 0, resolution, resolution);
  root.unmount(); document.body.removeChild(tempDiv);
  return canvas.toDataURL('image/png');
};

const drawCard = (pdf, font, qrImg, logo, x, y, w, h, companyName, colors, subtitle) => {
  const s = h / 297; const cx = x + w / 2;
  const c1 = hexToRgb(colors.primary); const c2 = hexToRgb(colors.secondary);
  const dark = luminance(c1) <= luminance(c2) ? c1 : c2;
  const light = luminance(c1) <= luminance(c2) ? c2 : c1;
  pdf.setFillColor(...dark); pdf.rect(x, y, w, h, 'F');
  const topMargin = 20 * s;
  if (logo?.dataUrl) {
    const maxW = 70 * s, maxH = 40 * s, natW = logo.width || 1, natH = logo.height || 1, ratio = natW / natH;
    let drawW, drawH;
    if (ratio >= maxW / maxH) { drawW = maxW; drawH = maxW / ratio; } else { drawH = maxH; drawW = maxH * ratio; }
    try { pdf.addImage(logo.dataUrl, logo.format || 'JPEG', cx - drawW / 2, y + topMargin + (maxH - drawH) / 2, drawW, drawH); }
    catch { /* cai no texto abaixo */ }
  } else {
    pdf.setFont(font, 'bold'); pdf.setFontSize(Math.max(6, 28 * s)); pdf.setTextColor(...light);
    pdf.text(companyName || '', cx, y + topMargin + 20 * s, { align: 'center' });
  }
  pdf.setFont(font, 'normal'); pdf.setFontSize(Math.max(5, 22 * s)); pdf.setTextColor(...light);
  pdf.text('Quer elogiar, criticar ou sugerir uma melhoria?', cx, y + 95 * s, { align: 'center' });
  pdf.setFont(font, 'bold'); pdf.setFontSize(Math.max(5, 24 * s)); pdf.setTextColor(...light);
  pdf.text(subtitle, cx, y + 110 * s, { align: 'center' });
  const qrSz = 85 * s, qrPad = 5 * s;
  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(cx - qrSz / 2 - qrPad, y + 135 * s - qrPad, qrSz + qrPad * 2, qrSz + qrPad * 2, 4 * s, 4 * s, 'F');
  pdf.addImage(qrImg, 'PNG', cx - qrSz / 2, y + 135 * s, qrSz, qrSz);
  pdf.setFont(font, 'normal'); pdf.setFontSize(Math.max(4, 18 * s)); pdf.setTextColor(...light);
  pdf.text('Aponte a camera do seu celular', cx, y + 235 * s, { align: 'center' });
  pdf.setFont(font, 'bold'); pdf.setFontSize(Math.max(3, 12 * s)); pdf.setTextColor(...light);
  pdf.text('falocomodono.com.br', cx, y + 280 * s, { align: 'center' });
};

export const PLAQUE_FORMATS = [
  { id: 1, label: 'Parede A4 (1 por folha)', file: 'parede-A4' },
  { id: 2, label: 'Mesa A5 (2 por folha)', file: 'mesa-A5-2x' },
  { id: 4, label: '4 por folha', file: '4-por-folha' },
  { id: 8, label: '8 por folha', file: '8-por-folha' },
  { id: 'balcao', label: 'Balcão 10x15cm', file: 'balcao-10x15cm' },
];

/** company: { name, slug, primary_color, secondary_color, owner_title, logo } */
export const generatePlaque = async (perSheet, company) => {
  const companyName = (company.name || 'NOSSA EMPRESA').toUpperCase();
  const url = `${window.location.origin}/${company.slug}`;
  const subtitle = { dona: 'Use o QR Code e fale direto com a Dona.', neutro: 'Use o QR Code e fale direto com quem manda.' }[company.owner_title]
    || 'Use o QR Code e fale direto com o Dono.';
  const colors = { primary: company.primary_color || '#000080', secondary: company.secondary_color || '#FFD700' };

  const qrImg = await getQRCodeImage(url, 1200);
  if (!qrImg) throw new Error('Não foi possível gerar o QR Code');

  if (perSheet === 'balcao') {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [100, 150] });
    const font = await loadFonts(pdf);
    drawCard(pdf, font, qrImg, company.logo, 0, 0, 100, 150, companyName, colors, subtitle);
    pdf.save(`placa-balcao-10x15cm-${company.slug}.pdf`);
    return;
  }

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const font = await loadFonts(pdf);
  const A4W = 210, A4H = 297;
  if (perSheet === 1) {
    drawCard(pdf, font, qrImg, company.logo, 0, 0, A4W, A4H, companyName, colors, subtitle);
  } else {
    const grids = { 2: { cols: 1, rows: 2, margin: 8, gap: 5 }, 4: { cols: 2, rows: 2, margin: 6, gap: 4 }, 8: { cols: 2, rows: 4, margin: 4, gap: 3 } };
    const { cols, rows, margin, gap } = grids[perSheet];
    const RATIO = A4W / A4H;
    const cellW = (A4W - margin * 2 - gap * (cols - 1)) / cols;
    const cellH = (A4H - margin * 2 - gap * (rows - 1)) / rows;
    let cardW, cardH;
    if (cellW / cellH > RATIO) { cardH = cellH; cardW = cardH * RATIO; } else { cardW = cellW; cardH = cardW / RATIO; }
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cellX = margin + c * (cellW + gap), cellY = margin + r * (cellH + gap);
        const cardX = cellX + (cellW - cardW) / 2, cardY = cellY + (cellH - cardH) / 2;
        pdf.setDrawColor(200, 200, 200); pdf.setLineWidth(0.15); pdf.setLineDashPattern([1.5, 1.5], 0);
        pdf.rect(cardX - 0.5, cardY - 0.5, cardW + 1, cardH + 1, 'S'); pdf.setLineDashPattern([], 0);
        drawCard(pdf, font, qrImg, company.logo, cardX, cardY, cardW, cardH, companyName, colors, subtitle);
      }
    }
  }
  const file = PLAQUE_FORMATS.find(f => f.id === perSheet)?.file || 'placa';
  pdf.save(`placa-${file}-${company.slug}.pdf`);
};
